import { Search, X } from 'lucide-react';
import { useId, useMemo, useRef, useState, type ReactElement } from 'react';
import {
  ACTIVITY_BUCKETS,
  COMPLETION_STATES,
  CUSTOM_FIELD_CHECKBOX_STATES,
  CUSTOM_FIELD_DATE_BUCKETS,
  CUSTOM_FIELD_TEXT_VALUE_MAX_LENGTH,
  DUE_BUCKETS,
  KEYWORD_MAX_LENGTH,
  NO_FACET_MATCH,
  type BoardMember,
  type CustomFieldCriterion,
  type CustomFieldDefinition,
  type Label,
} from '@molt/shared';
import {
  Avatar,
  Button,
  FacetOption,
  IconButton,
  LabelChip,
  Popover,
  TextField,
  profileName,
} from '@/components';
import { labelColourDisplayName } from '@/components/palette';
import {
  EMPTY_BOARD_FILTER,
  activityBucketFilterLabel,
  boardFilterActiveCount,
  boardFilterLabelFormatter,
  completionFilterLabel,
  customFieldCriterionLabel,
  dueBucketFilterLabel,
  isBoardFilterCriterionSelected,
  labelFilterLabel,
  memberFilterLabel,
  setBoardFilterActivity,
  setBoardFilterCriterion,
  setBoardFilterKeyword,
  toggleBoardFilterCriterion,
  toggleBoardFilterValue,
  type BoardFilterSelection,
} from '@/lib/board-filter';
import { useInterfaceLanguage } from '@/lib/interface-language';

export interface BoardFilterPanelProps {
  boardTitle: string;
  filter: BoardFilterSelection;
  members: readonly BoardMember[];
  labels: readonly Label[];
  /**
   * `FR-SEARCH-031` — every custom field DEFINED on the board, hidden ones
   * included and in the definitions' own order. This component is what drops
   * the hidden ones; handing it a pre-filtered list would move that decision
   * to each caller and lose the one place it is stated.
   */
  customFields?: readonly CustomFieldDefinition[] | undefined;
  signedIn: boolean;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onFilterChange: (filter: BoardFilterSelection) => void;
}

export function BoardFilterPanel({
  boardTitle,
  filter,
  members,
  labels,
  customFields = [],
  signedIn,
  open,
  onOpenChange,
  onFilterChange,
}: BoardFilterPanelProps): ReactElement {
  const { t } = useInterfaceLanguage();
  const activityGroupName = useId();
  const customFieldHeadingPrefix = useId();
  const keywordRef = useRef<HTMLInputElement>(null);
  const memberSearchRef = useRef<HTMLInputElement>(null);
  const labelSearchRef = useRef<HTMLInputElement>(null);
  const [memberQuery, setMemberQuery] = useState('');
  const [labelQuery, setLabelQuery] = useState('');
  /**
   * What the member has TYPED into each numeric field's box, which is not the
   * same thing as the criterion it produced.
   *
   * ⚠ A half-typed number — `-`, `3.`, `1e` — is not a finite number, so it
   * produces no criterion. Rendering the box from the criterion alone would
   * therefore delete those characters as they were typed, and a field would be
   * impossible to fill in with anything but a bare integer. The draft is the
   * displayed value; the criterion is set when (and only when) the draft
   * parses, and cleared when the box is emptied.
   */
  const [numberDrafts, setNumberDrafts] = useState<Record<string, string>>({});
  const activeCount = boardFilterActiveCount(filter);

  const shownMembers = useMemo(() => {
    const query = memberQuery.trim().toLowerCase();
    if (query === '') return members;
    return members.filter((member) =>
      `${profileName(member)} ${member.username} @${member.username}`
        .toLowerCase()
        .includes(query),
    );
  }, [memberQuery, members]);

  const formatter = useMemo(
    () =>
      boardFilterLabelFormatter(t, {
        labelColour: (colour) => labelColourDisplayName(t, colour),
      }),
    [t],
  );

  const shownLabels = useMemo(() => {
    const query = labelQuery.trim().toLowerCase();
    if (query === '') return labels;
    return labels.filter((label) =>
      labelFilterLabel(label.id, labels, formatter).toLowerCase().includes(query),
    );
  }, [formatter, labelQuery, labels]);

  /**
   * `FR-SEARCH-031`: "Hidden fields (`FR-CARD-027`) offer no facet, because
   * they present nowhere else either."
   *
   * ⚠ **This is only HALF of AC5.** Dropping the control here makes the facet
   * disappear; it does not make the criterion stop constraining the board. The
   * other half is `pruneBoardFilterFacets`, run by the board route against the
   * same definitions, which removes the criterion from the filter and from the
   * URL. A panel that hid the control and left the criterion is the exact
   * failure AC5 names — a board narrowed by an invisible filter.
   *
   * The order is the array's, which is the server's `ORDER BY position, id`
   * (`apps/server/src/custom-fields/read.ts`) — "the definitions' own order",
   * not re-sorted here.
   */
  const shownCustomFields = useMemo(
    () => customFields.filter((field) => !field.hidden),
    [customFields],
  );

  const triggerLabel =
    activeCount === 0
      ? t('boardFace.filters.trigger.emptyLabel')
      : t('boardFace.filters.trigger.activeLabel', { count: activeCount }, activeCount);

  return (
    <Popover
      open={open}
      onOpenChange={onOpenChange}
      title={t('boardFace.filters.panel.title')}
      width={320}
      initialFocus={keywordRef}
      closeControl={
        <IconButton label={t('boardFace.filters.panel.close')} variant="quiet" size="sm">
          <X aria-hidden="true" className="size-4" />
        </IconButton>
      }
      trigger={
        <Button
          type="button"
          size="sm"
          variant={activeCount === 0 ? 'quiet' : 'primary'}
          aria-label={triggerLabel}
          aria-pressed={activeCount > 0}
          className={activeCount === 0 ? 'text-on-board' : ''}
          iconStart={<Search aria-hidden="true" className="size-4" />}
        >
          {activeCount === 0
            ? t('boardFace.filters.filter')
            : t('boardFace.filters.filtered')}
        </Button>
      }
      footer={
        <Button
          type="button"
          size="sm"
          variant="quiet"
          disabled={activeCount === 0}
          onClick={() => onFilterChange(EMPTY_BOARD_FILTER)}
        >
          {t('boardFace.filters.panel.clear')}
        </Button>
      }
    >
      <div
        className="flex flex-col gap-4"
        aria-label={t('boardFace.filters.panel.region', { board: boardTitle })}
      >
        <section aria-labelledby="board-filter-keyword-heading" className="space-y-2">
          <h3 id="board-filter-keyword-heading" className="text-sm font-medium text-ink">
            {t('boardFace.filters.keyword.heading')}
          </h3>
          <TextField
            ref={keywordRef}
            label={t('boardFace.filters.keyword.label')}
            value={filter.keyword}
            maxLength={KEYWORD_MAX_LENGTH}
            clearable
            onClear={() => onFilterChange(setBoardFilterKeyword(filter, ''))}
            onChange={(event) =>
              onFilterChange(setBoardFilterKeyword(filter, event.currentTarget.value))
            }
          />
        </section>

        <section aria-labelledby="board-filter-members-heading" className="space-y-2">
          <h3 id="board-filter-members-heading" className="text-sm font-medium text-ink">
            {t('boardFace.filters.members.heading')}
          </h3>
          {signedIn ? (
            <>
              <TextField
                ref={memberSearchRef}
                label={t('boardFace.filters.members.searchLabel')}
                value={memberQuery}
                clearable
                onClear={() => setMemberQuery('')}
                onChange={(event) => setMemberQuery(event.currentTarget.value)}
              />
              <div className="space-y-1">
                <FacetOption
                  control="checkbox"
                  label={formatter.noMembers()}
                  checked={filter.members.includes(NO_FACET_MATCH)}
                  onChange={() =>
                    onFilterChange(
                      toggleBoardFilterValue(filter, 'members', NO_FACET_MATCH),
                    )
                  }
                />
                {shownMembers.map((member) => (
                  <FacetOption
                    key={member.id}
                    control="checkbox"
                    label={memberFilterLabel(member.id, members, formatter)}
                    leading={<Avatar profile={member} size="xs" decorative />}
                    leadingKind="avatar"
                    checked={filter.members.includes(member.id)}
                    onChange={() =>
                      onFilterChange(toggleBoardFilterValue(filter, 'members', member.id))
                    }
                  />
                ))}
              </div>
            </>
          ) : (
            <p className="rounded-card bg-surface-raised px-2 py-1.5 text-sm text-ink">
              {t('boardFace.filters.members.signInRequired')}
            </p>
          )}
        </section>

        <section aria-labelledby="board-filter-labels-heading" className="space-y-2">
          <h3 id="board-filter-labels-heading" className="text-sm font-medium text-ink">
            {t('boardFace.filters.labels.heading')}
          </h3>
          <TextField
            ref={labelSearchRef}
            label={t('boardFace.filters.labels.searchLabel')}
            value={labelQuery}
            clearable
            onClear={() => setLabelQuery('')}
            onChange={(event) => setLabelQuery(event.currentTarget.value)}
          />
          <div className="space-y-1">
            <FacetOption
              control="checkbox"
              label={formatter.noLabels()}
              checked={filter.labels.includes(NO_FACET_MATCH)}
              onChange={() =>
                onFilterChange(toggleBoardFilterValue(filter, 'labels', NO_FACET_MATCH))
              }
            />
            {shownLabels.map((label) => (
              <FacetOption
                key={label.id}
                control="checkbox"
                label={labelFilterLabel(label.id, labels, formatter)}
                leading={
                  <LabelChip
                    colour={label.colour}
                    name={label.name === '' ? null : label.name}
                    size="sm"
                    showName={label.name !== null && label.name !== ''}
                  />
                }
                leadingKind="label-chip"
                checked={filter.labels.includes(label.id)}
                onChange={() =>
                  onFilterChange(toggleBoardFilterValue(filter, 'labels', label.id))
                }
              />
            ))}
          </div>
        </section>

        <section aria-labelledby="board-filter-activity-heading" className="space-y-2">
          <h3 id="board-filter-activity-heading" className="text-sm font-medium text-ink">
            {t('boardFace.filters.activity.heading')}
          </h3>
          <div className="space-y-1">
            <FacetOption
              control="radio"
              name={activityGroupName}
              label={t('boardFace.filters.activity.anyAge')}
              checked={filter.activity === null}
              onChange={() => onFilterChange(setBoardFilterActivity(filter, null))}
            />
            {ACTIVITY_BUCKETS.map((bucket) => (
              <FacetOption
                key={bucket}
                control="radio"
                name={activityGroupName}
                label={activityBucketFilterLabel(bucket, formatter)}
                checked={filter.activity === bucket}
                onChange={() => onFilterChange(setBoardFilterActivity(filter, bucket))}
              />
            ))}
          </div>
        </section>

        <section aria-labelledby="board-filter-due-heading" className="space-y-2">
          <h3 id="board-filter-due-heading" className="text-sm font-medium text-ink">
            {t('boardFace.filters.due.heading')}
          </h3>
          <div className="space-y-1">
            {DUE_BUCKETS.map((bucket) => (
              <FacetOption
                key={bucket}
                control="checkbox"
                label={dueBucketFilterLabel(bucket, formatter)}
                checked={filter.due.includes(bucket)}
                onChange={() =>
                  onFilterChange(toggleBoardFilterValue(filter, 'due', bucket))
                }
              />
            ))}
          </div>
        </section>

        <section aria-labelledby="board-filter-completion-heading" className="space-y-2">
          <h3
            id="board-filter-completion-heading"
            className="text-sm font-medium text-ink"
          >
            {t('boardFace.filters.completion.heading')}
          </h3>
          <div className="space-y-1">
            {COMPLETION_STATES.map((state) => (
              <FacetOption
                key={state}
                control="checkbox"
                label={completionFilterLabel(state, formatter)}
                checked={filter.completion.includes(state)}
                onChange={() =>
                  onFilterChange(toggleBoardFilterValue(filter, 'completion', state))
                }
              />
            ))}
          </div>
        </section>
        {shownCustomFields.map((field) => {
          const headingId = `${customFieldHeadingPrefix}-${field.id}`;
          const textCriterion = filter.customFields.find(
            (criterion) => criterion.fieldId === field.id && criterion.kind === 'text',
          );
          const numberCriterion = filter.customFields.find(
            (criterion) => criterion.fieldId === field.id && criterion.kind === 'number',
          );
          const optionLabel = (criterion: CustomFieldCriterion): string =>
            customFieldCriterionLabel(criterion, customFields, formatter);
          const toggle = (criterion: CustomFieldCriterion): void =>
            onFilterChange(toggleBoardFilterCriterion(filter, criterion));

          return (
            <section key={field.id} aria-labelledby={headingId} className="space-y-2">
              <h3 id={headingId} className="text-sm font-medium text-ink">
                {field.name}
              </h3>

              {field.type === 'checkbox' && (
                <div className="space-y-1">
                  {CUSTOM_FIELD_CHECKBOX_STATES.map((state) => {
                    const criterion: CustomFieldCriterion = {
                      fieldId: field.id,
                      kind: 'checkbox',
                      state,
                    };
                    return (
                      <FacetOption
                        key={state}
                        control="checkbox"
                        label={optionLabel(criterion)}
                        checked={isBoardFilterCriterionSelected(filter, criterion)}
                        onChange={() => toggle(criterion)}
                      />
                    );
                  })}
                </div>
              )}

              {field.type === 'dropdown' && (
                <div className="space-y-1">
                  {field.options.map((option) => {
                    const criterion: CustomFieldCriterion = {
                      fieldId: field.id,
                      kind: 'option',
                      optionId: option.id,
                    };
                    return (
                      <FacetOption
                        key={option.id}
                        control="checkbox"
                        label={option.label}
                        checked={isBoardFilterCriterionSelected(filter, criterion)}
                        onChange={() => toggle(criterion)}
                      />
                    );
                  })}
                </div>
              )}

              {field.type === 'date' && (
                <div className="space-y-1">
                  {CUSTOM_FIELD_DATE_BUCKETS.map((bucket) => {
                    const criterion: CustomFieldCriterion = {
                      fieldId: field.id,
                      kind: 'date',
                      bucket,
                    };
                    return (
                      <FacetOption
                        key={bucket}
                        control="checkbox"
                        label={dueBucketFilterLabel(bucket, formatter)}
                        checked={isBoardFilterCriterionSelected(filter, criterion)}
                        onChange={() => toggle(criterion)}
                      />
                    );
                  })}
                </div>
              )}

              {field.type === 'text' && (
                <TextField
                  label={t('boardFace.filters.customField.containsLabel', {
                    field: field.name,
                  })}
                  value={textCriterion?.kind === 'text' ? textCriterion.text : ''}
                  maxLength={CUSTOM_FIELD_TEXT_VALUE_MAX_LENGTH}
                  clearable
                  onClear={() =>
                    onFilterChange(
                      setBoardFilterCriterion(filter, field.id, 'text', null),
                    )
                  }
                  onChange={(event) => {
                    const text = event.currentTarget.value;
                    onFilterChange(
                      setBoardFilterCriterion(
                        filter,
                        field.id,
                        'text',
                        text === '' ? null : { fieldId: field.id, kind: 'text', text },
                      ),
                    );
                  }}
                />
              )}

              {field.type === 'number' && (
                <TextField
                  label={t('boardFace.filters.customField.equalsLabel', {
                    field: field.name,
                  })}
                  inputMode="decimal"
                  value={
                    numberDrafts[field.id] ??
                    (numberCriterion?.kind === 'number'
                      ? String(numberCriterion.value)
                      : '')
                  }
                  clearable
                  onClear={() => {
                    setNumberDrafts((drafts) => ({ ...drafts, [field.id]: '' }));
                    onFilterChange(
                      setBoardFilterCriterion(filter, field.id, 'number', null),
                    );
                  }}
                  onChange={(event) => {
                    const draft = event.currentTarget.value;
                    setNumberDrafts((drafts) => ({ ...drafts, [field.id]: draft }));
                    const value = Number(draft);
                    onFilterChange(
                      setBoardFilterCriterion(
                        filter,
                        field.id,
                        'number',
                        draft.trim() === '' || !Number.isFinite(value)
                          ? null
                          : { fieldId: field.id, kind: 'number', value },
                      ),
                    );
                  }}
                />
              )}
            </section>
          );
        })}
      </div>
    </Popover>
  );
}
